import { Button } from '@/components/atoms/NewPcButton';
import AutoCloseOverlay from '@/components/atoms/AutoCloseOverlay';
import Input from '@/components/V3/Input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { CPLAT_API_ROUTES, usePost } from '@/api';
import { ENTERED_BY_MARKET_NAME } from '@/constants/localstorage';
import { useLogEvent } from '@/hooks/use-log-event';
import { useMarketRegisterToken } from '@/hooks/use-market-register-token';
import { ROUTES } from '@/routes';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';

const formSchema = z.object({
  email: z.email('올바르지 않은 이메일 형식 입니다'),
  password: z.string(),
});

type FormType = z.infer<typeof formSchema>;

export const SigninEmailForm = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const autoClose = searchParams.get('autoclose') === 'true';
  const redirectPath = searchParams.get('redirect');
  const [, setLocalStorageCplatToken] = useLocalStorage('cplatToken', '');
  const [showClosing, setShowClosing] = useState(false);
  const enteredByMarketName = localStorage.getItem(ENTERED_BY_MARKET_NAME);
  const { logEvent } = useLogEvent();

  const { onConnect } = useMarketRegisterToken();

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    setError,
  } = useForm<FormType>({ resolver: zodResolver(formSchema) });

  const watchedEmail = watch('email');
  const watchedPassword = watch('password');

  const { mutate: onSigninByEmail } = usePost(
    CPLAT_API_ROUTES.signinByEmail,
    undefined,
    undefined,
    true,
  );

  const handleLogin = (values: FormType) => {
    onSigninByEmail(
      { accountEmail: values.email, plainPassword: values.password },
      {
        onSuccess: (response) => {
          const cplatToken = (response.data.data[0] as { cplatToken: string }).cplatToken;
          setLocalStorageCplatToken(cplatToken);
          localStorage.setItem('cplatToken', JSON.stringify(cplatToken));
          if (autoClose) {
            setShowClosing(true);
            setTimeout(() => {
              if (window.opener || window.history.length <= 1) {
                window.close();
              }
              window.location.replace(ROUTES.dashboard);
            }, 1500);
            return;
          }
          if (enteredByMarketName) onConnect(cplatToken);
          else {
            localStorage.removeItem(ENTERED_BY_MARKET_NAME);
            if (redirectPath) {
              window.location.replace(redirectPath);
            } else {
              navigate(ROUTES.root);
            }
          }
        },
        onError: (error) => {
          const errorMessage = (error.response?.data as { message: string })?.message;
          setError('password', { message: errorMessage });
        },
      },
    );
  };

  return (
    <>
    {showClosing && <AutoCloseOverlay />}
    <form
      className="space-y-8"
      onSubmit={handleSubmit(handleLogin)} // ← 엔터 입력 시 자동 submit
    >
      <div className="space-y-3">
        <div className="flex flex-col items-start gap-2">
          <span className="text-gr-800 Caption1M12">이메일</span>
          <Input
            placeholder="이메일 주소를 입력해 주세요"
            {...register('email')}
            Error={{ isError: !!errors.email?.message, errorMessage: errors.email?.message }}
          />
        </div>
        <div className="flex flex-col items-start gap-2">
          <span className="text-gr-800 Caption1M12">비밀번호</span>
          <Input
            placeholder="비밀번호를 입력해 주세요"
            {...register('password')}
            type="password"
            Error={{ isError: !!errors.password?.message, errorMessage: errors.password?.message }}
          />
        </div>
        <div className="flex justify-end">
          <button
            className="text-gr-700 underline underline-offset-2 Body5M14"
            type="button"
            onClick={() => navigate(ROUTES.resetPassword)}
          >
            비밀번호 재설정
          </button>
        </div>
      </div>

      <Button
        className="w-full"
        type="submit" // ← submit 버튼
        disabled={!watchedEmail || !watchedPassword}
      >
        로그인
      </Button>
    </form>
    </>
  );
};
